import { isPlatformBrowser } from '@angular/common';
import type { OnDestroy, OnInit } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { FileOpener } from '@awesome-cordova-plugins/file-opener/ngx';
import { PreviewAnyFile } from '@awesome-cordova-plugins/preview-any-file/ngx';
import { Share } from '@capacitor/share';
import { Media } from '@capacitor-community/media';
import { Pwa } from '@freelancer/pwa';
import { TimeUtils } from '@freelancer/time-utils';
import {
  ToastAlertColor,
  ToastAlertService,
  ToastAlertType,
} from '@freelancer/ui/toast-alert';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { firstValueFrom, Subscription } from 'rxjs';
import { FileDownloadAlertContentComponent } from './file-download-alert-content.component';
import type { DownloadTask } from './file-download.service';
import { FileDownload } from './file-download.service';

// Type-safe interface for FileDownloadAlertContentComponent inputs
interface FileDownloadAlertContentInputs {
  download: DownloadTask;
  onCancel(): void;
  onOpen(): void;
}

@UntilDestroy({ className: 'FileDownloadAlertComponent' })
@Component({
  standalone: false,
  selector: 'fl-file-download-alert',
  template: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FileDownloadAlertComponent implements OnInit, OnDestroy {
  private fileDownload = inject(FileDownload);
  private toastAlertService = inject(ToastAlertService);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private fileOpener = inject(FileOpener);
  private previewAnyFile = inject(PreviewAnyFile);
  private pwa = inject(Pwa);
  private timeUtils = inject(TimeUtils);
  private platformId = inject(PLATFORM_ID);

  downloads: readonly DownloadTask[] = [];
  /**
   * Whether the app should use Capacitor Media plugin to save directly to the
   * gallery for compatible file types (video/photo), or open the share modal
   * for incompatible file types on download completion.
   * Needed on iOS because files cannot be saved to public locations,
   * whereas on Android we just save the files to /Downloads.
   */
  requiresSaving: boolean;
  // Store file URLs for completed downloads
  private completedDownloads = new Map<string, string>();

  private subscriptions = new Subscription();

  async ngOnInit(): Promise<void> {
    this.requiresSaving = this.pwa.getPlatform() === 'ios';

    if (isPlatformBrowser(this.platformId) && this.pwa.isNative()) {
      this.subscriptions.add(
        this.fileDownload.getDownloadTaskStream().subscribe(async download => {
          this.downloads = [...this.downloads, download];

          const componentConfig = {
            component: FileDownloadAlertContentComponent,
            inputs: {
              download,
              onCancel: () => this.handleCancel(download),
              onOpen: () => this.handleOpen(download),
            },
          };
          this.toastAlertService.openWithCustomComponent<FileDownloadAlertContentInputs>(
            componentConfig,
            {
              id: `fl-download-alert-${download.id}`,
              type: ToastAlertType.LOADING,
              color: ToastAlertColor.LIGHT,
              indefinite: true,
              closeable: false,
            },
          );

          // Handle download status changes
          this.handleDownloadStatusChanges(download);

          this.changeDetectorRef.markForCheck();
        }),
      );
    }
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  handleCancel(download: DownloadTask): void {
    download.cancel();
    this.handleHide(download);
  }

  handleHide(download: DownloadTask): void {
    this.toastAlertService.close(`fl-download-alert-${download.id}`);
    this.downloads = this.downloads.filter(task => task.id !== download.id);
    // Clean up stored file URL
    this.completedDownloads.delete(download.id);
  }

  private handleDownloadStatusChanges(download: DownloadTask): void {
    this.subscriptions.add(
      download.changes$.subscribe(change => {
        const toastId = `fl-download-alert-${download.id}`;
        const toastInstance = (this.toastAlertService as any).toastAlerts[
          toastId
        ];

        if (toastInstance) {
          // Update toast type and closeable state based on current status
          // eslint-disable-next-line default-case
          switch (change.status) {
            case 'running': {
              toastInstance.type = ToastAlertType.LOADING;
              toastInstance.closeable = false;
              break;
            }
            case 'success': {
              toastInstance.type = ToastAlertType.SUCCESS;
              toastInstance.closeable = true;

              // Store the file URL for later use in handleOpen
              if (change.fileUrl) {
                this.completedDownloads.set(download.id, change.fileUrl);
              }

              if (this.requiresSaving) {
                this.handleSave(download, change.fileUrl);
              } else {
                this.timeUtils.setTimeout(() => {
                  this.handleHide(download);
                }, 5000);
              }
              break;
            }
            case 'error': {
              toastInstance.type = ToastAlertType.ERROR;
              toastInstance.closeable = true;
              break;
            }
          }
        }
      }),
    );
  }

  async handleSave(download: DownloadTask, fileUrl: string): Promise<void> {
    const mimeType = await firstValueFrom(
      download.mimeType$.pipe(untilDestroyed(this)),
    );
    const useCapacitorMedia =
      mimeType.startsWith('video/') || mimeType.startsWith('image/');

    // Timeout to be set dependent on if the user will save or share.
    const timeout = useCapacitorMedia ? 5000 : 1000;

    if (useCapacitorMedia) {
      if (mimeType.startsWith('video/')) {
        Media.saveVideo({ path: fileUrl });
      } else if (mimeType.startsWith('image/')) {
        Media.savePhoto({ path: fileUrl });
      }
    } else {
      await Share.share({
        title: download.name,
        url: fileUrl,
      }).catch(error => {
        // User changes their mind. Ignore the error.
        if (error.message === 'Share canceled') {
          return;
        }
        throw error;
      });
    }

    // If the user shared, we can close after a short delay.
    this.timeUtils.setTimeout(() => {
      this.handleHide(download);
    }, timeout);
  }

  async handleOpen(download: DownloadTask): Promise<void> {
    // Get the stored file URL from our completed downloads map
    const fileUrl = this.completedDownloads.get(download.id);

    if (!fileUrl) {
      return;
    }

    const platform = this.pwa.getPlatform();
    // If we're in iOS, then open it up in the file preview window.
    if (platform === 'ios') {
      await firstValueFrom(
        this.previewAnyFile.previewPath(fileUrl).pipe(untilDestroyed(this)),
      );
    } else if (platform === 'android') {
      // Otherwise, on Android use the file opener
      const mimeType = await firstValueFrom(
        download.mimeType$.pipe(untilDestroyed(this)),
      );
      this.fileOpener.showOpenWithDialog(
        fileUrl,
        mimeType ||
          // In the case there's no mime type and it is running on Android,
          // attempt to open it in a file manager.
          'vnd.android.document/directory',
      );
    }

    // Close the prompt shortly after the user has opened.
    this.timeUtils.setTimeout(() => {
      this.handleHide(download);
    }, 1000);
  }
}
